import {
    Autocomplete,
    Box,
    Button,
    FormControlLabel,
    MenuItem,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { standaloneFieldSx, standaloneFloatingSelectSx } from '../../common/fieldStyles';
import { httpClient } from '../../common/httpclient';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import useIsMobileShell from '../../hooks/useIsMobileShell';
import MobileInventoryForm from '../../component/inventory/MobileInventoryForm';
import { inventoryErrorMessage } from './inventoryStatus';

// Autocomplete multi-seleção (chips): altura livre em vez dos 52px fixos do padrão.
const multiFieldSx = {
    ...standaloneFieldSx,
    '& .MuiOutlinedInput-root': {
        ...standaloneFieldSx['& .MuiOutlinedInput-root'],
        height: 'auto',
        minHeight: '52px',
    },
};

const validationSchema = Yup.object({
    description: Yup.string().max(500, 'Máximo de 500 caracteres'),
    location_label: Yup.string().max(255, 'Máximo de 255 caracteres'),
    scope: Yup.string().oneOf(['full', 'partial']).required(),
});

const asList = (data, key) => {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data[key])) return data[key];
    return [];
};

export default function Form() {
    const navigate = useNavigate();
    const isMobileShell = useIsMobileShell();

    const [categories, setCategories] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [products, setProducts] = useState([]);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('error');

    useEffect(() => {
        httpClient.get('/stock/categories').then((r) => setCategories(asList(r.data, 'categories'))).catch(() => setCategories([]));
        httpClient.get('/stock/suppliers').then((r) => setSuppliers(asList(r.data, 'suppliers'))).catch(() => setSuppliers([]));
        httpClient.get('/stock/all').then((r) => setProducts(asList(r.data, 'items'))).catch(() => setProducts([]));
    }, []);

    const formik = useFormik({
        initialValues: {
            description: '',
            location_label: '',
            scope: 'full',
            categories: [],
            suppliers: [],
            products: [],
            code: '',
            only_positive_stock: false,
            only_no_movement: false,
            only_below_min: false,
        },
        validationSchema,
        onSubmit: async (values, { setSubmitting }) => {
            const partial = values.scope === 'partial';
            const hasFilter = values.categories.length > 0
                || values.suppliers.length > 0
                || values.products.length > 0
                || values.code.trim() !== ''
                || values.only_positive_stock
                || values.only_no_movement
                || values.only_below_min;

            if (partial && !hasFilter) {
                setSnackbarMessage('Informe ao menos um filtro para o inventário parcial.');
                setSnackbarSeverity('error');
                setOpenSnackbar(true);
                setSubmitting(false);
                return;
            }

            try {
                const response = await httpClient.post('/inventories', {
                    description: values.description,
                    location_label: values.location_label,
                    scope: values.scope,
                    filters: {
                        category_ids: values.categories.map((c) => c.id),
                        supplier_ids: values.suppliers.map((s) => s.id),
                        product_ids: values.products.map((p) => p.id),
                        code: values.code,
                        only_positive_stock: values.only_positive_stock,
                        only_no_movement: values.only_no_movement,
                        only_below_min: values.only_below_min,
                    },
                });
                navigate(`/inventory/${response.data.id}`);
            } catch (error) {
                console.error('Error creating inventory:', error);
                setSnackbarMessage(inventoryErrorMessage(error, 'Erro ao criar inventário.'));
                setSnackbarSeverity('error');
                setOpenSnackbar(true);
            } finally {
                setSubmitting(false);
            }
        },
    });

    const partial = formik.values.scope === 'partial';

    // Shell mobile: formulário do protótipo `inventario/form.html` (mesmos campos, validação e envio).
    if (isMobileShell) {
        return (
            <PageScaffold
                links={[{ title: 'Inventário de Estoque', link: '/inventory' }]}
                titlePageTarget="Novo inventário"
                openSnackbar={openSnackbar}
                setOpenSnackbar={setOpenSnackbar}
                message={snackbarMessage}
                severity={snackbarSeverity}
            >
                <MobileInventoryForm
                    formik={formik}
                    categories={categories}
                    suppliers={suppliers}
                    products={products}
                    onCancel={() => navigate('/inventory')}
                />
            </PageScaffold>
        );
    }

    return (
        <PageScaffold
            links={[{ title: 'Inventário de Estoque', link: '/inventory' }]}
            titlePageTarget="Novo inventário"
            openSnackbar={openSnackbar}
            setOpenSnackbar={setOpenSnackbar}
            message={snackbarMessage}
            severity={snackbarSeverity}
        >
            <Box component="form" onSubmit={formik.handleSubmit} sx={{ width: '100%', maxWidth: 900, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography component="h1" sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em' }}>
                    Novo inventário
                </Typography>

                <TextField
                    name="description"
                    label="Descrição / observação"
                    multiline
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.description && Boolean(formik.errors.description)}
                    helperText={formik.touched.description && formik.errors.description}
                    sx={standaloneFieldSx}
                />

                <TextField
                    name="location_label"
                    label="Estoque / local"
                    value={formik.values.location_label}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.location_label && Boolean(formik.errors.location_label)}
                    helperText={formik.touched.location_label && formik.errors.location_label}
                    sx={standaloneFieldSx}
                />

                <TextField
                    select
                    name="scope"
                    label="Tipo de inventário"
                    value={formik.values.scope}
                    onChange={formik.handleChange}
                    sx={standaloneFloatingSelectSx}
                >
                    <MenuItem value="full">Completo (todos os produtos)</MenuItem>
                    <MenuItem value="partial">Parcial (somente produtos filtrados)</MenuItem>
                </TextField>

                {partial && (
                    <>
                        <Autocomplete
                            multiple
                            options={categories}
                            getOptionLabel={(option) => option.name || ''}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            value={formik.values.categories}
                            onChange={(_, value) => formik.setFieldValue('categories', value)}
                            renderInput={(params) => <TextField {...params} label="Categorias" sx={multiFieldSx} />}
                        />
                        <Autocomplete
                            multiple
                            options={suppliers}
                            getOptionLabel={(option) => option.name || ''}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            value={formik.values.suppliers}
                            onChange={(_, value) => formik.setFieldValue('suppliers', value)}
                            renderInput={(params) => <TextField {...params} label="Fornecedores" sx={multiFieldSx} />}
                        />
                        <Autocomplete
                            multiple
                            options={products}
                            getOptionLabel={(option) => (option.code ? `${option.code} - ${option.name}` : option.name || '')}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            value={formik.values.products}
                            onChange={(_, value) => formik.setFieldValue('products', value)}
                            renderInput={(params) => <TextField {...params} label="Produtos" sx={multiFieldSx} />}
                        />
                        <TextField
                            name="code"
                            label="Código / SKU (contém)"
                            value={formik.values.code}
                            onChange={formik.handleChange}
                            sx={standaloneFieldSx}
                        />
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                            <FormControlLabel
                                control={<Switch name="only_positive_stock" checked={formik.values.only_positive_stock} onChange={formik.handleChange} />}
                                label="Somente com estoque positivo"
                            />
                            <FormControlLabel
                                control={<Switch name="only_no_movement" checked={formik.values.only_no_movement} onChange={formik.handleChange} />}
                                label="Sem movimentação (90 dias)"
                            />
                            <FormControlLabel
                                control={<Switch name="only_below_min" checked={formik.values.only_below_min} onChange={formik.handleChange} />}
                                label="Abaixo do estoque mínimo"
                            />
                        </Box>
                    </>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button variant="outlined" onClick={() => navigate('/inventory')}>Cancelar</Button>
                    <Button type="submit" variant="contained" disabled={formik.isSubmitting}>
                        {formik.isSubmitting ? 'Criando...' : 'Criar inventário'}
                    </Button>
                </Box>
            </Box>
        </PageScaffold>
    );
}
